<template>
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
  <div
    ref="rootEl"
    class="relative w-full overflow-hidden select-none"
    @focusin="isHovered = true"
    @mouseenter="isHovered = true"
    @focusout="isHovered = false"
    @mouseleave="isHovered = false"
    @pointerup="toggleTouchPause"
  >
    <div ref="trackEl" class="flex items-center will-change-transform">
      <ul
        ref="setAEl"
        class="flex shrink-0 list-none items-center"
        :style="setStyle"
      >
        <slot />
      </ul>

      <ul
        v-for="copy in duplicateSets"
        :key="copy"
        aria-hidden="true"
        class="flex shrink-0 list-none items-center"
        :style="setStyle"
      >
        <slot />
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

const props = withDefaults(
  defineProps<{
    speed?: number; // px/s
    gap?: number; // px
    pauseOnHover?: boolean;
  }>(),
  {
    speed: 40,
    gap: 64,
    pauseOnHover: true,
  },
);

const rootEl = ref<HTMLElement | null>(null);
const trackEl = ref<HTMLElement | null>(null);
const setAEl = ref<HTMLElement | null>(null);
const duplicateSets = ref(1);

const isHovered = ref(false);
const isTouchPaused = ref(false);
const prefersReducedMotion = ref(false);
let reducedMotionQuery: MediaQueryList | null = null;

function syncReducedMotion(query: MediaQueryList | MediaQueryListEvent): void {
  prefersReducedMotion.value = query.matches;
}

function addReducedMotionListener(query: MediaQueryList): void {
  if (typeof query.addEventListener === "function") {
    query.addEventListener("change", syncReducedMotion);
  } else {
    query.addListener(syncReducedMotion);
  }
}

function removeReducedMotionListener(query: MediaQueryList): void {
  if (typeof query.removeEventListener === "function") {
    query.removeEventListener("change", syncReducedMotion);
  } else {
    query.removeListener(syncReducedMotion);
  }
}

function toggleTouchPause(event: PointerEvent): void {
  if (event.pointerType === "touch") {
    isTouchPaused.value = !isTouchPaused.value;
  }
}

const isPaused = computed(
  () =>
    prefersReducedMotion.value ||
    isTouchPaused.value ||
    (props.pauseOnHover && isHovered.value),
);

const setStyle = computed(() => ({
  gap: `${props.gap}px`,
  paddingRight: `${props.gap}px`, // <-- critical: gap between end of A and start of B
}));

let rafId: number | null = null;
let ro: ResizeObserver | null = null;

let lastTs = 0;
let x = 0;
let setWidth = 0;

function measure(): void {
  const setA = setAEl.value;
  if (!setA) return;

  // scrollWidth includes internal flex gaps + paddingRight
  const w = Math.ceil(setA.scrollWidth);
  if (w <= 0) return;
  duplicateSets.value = Math.max(
    1,
    Math.ceil((rootEl.value?.clientWidth ?? 0) / w),
  );

  // Keep x stable across re-measures
  setWidth = w;
  x = ((x % setWidth) + setWidth) % setWidth;
}

async function measureAfterImages(): Promise<void> {
  const root = rootEl.value;
  if (!root) return;

  const imgs = Array.from(root.querySelectorAll("img"));

  await Promise.all(
    imgs.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const done = (): void => resolve();
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      });
    }),
  );

  // Wait for layout to settle
  await nextTick();
  requestAnimationFrame(() => {
    lastTs = 0; // avoid dt spike
    measure();
  });
}

function frame(ts: number): void {
  const track = trackEl.value;

  if (!track || setWidth <= 0) {
    rafId = requestAnimationFrame(frame);
    return;
  }

  if (!lastTs) lastTs = ts;
  const dt = (ts - lastTs) / 1000;
  lastTs = ts;

  if (!isPaused.value) {
    x += props.speed * dt;
    // modulo wrap (no visible jump if widths match)
    if (x >= setWidth) x -= setWidth;
  }

  track.style.transform = `translate3d(${-x}px, 0, 0)`;
  rafId = requestAnimationFrame(frame);
}

onMounted(async () => {
  reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  syncReducedMotion(reducedMotionQuery);
  addReducedMotionListener(reducedMotionQuery);

  await nextTick();
  measure();
  await measureAfterImages();

  ro = new ResizeObserver(() => {
    lastTs = 0;
    measure();
  });

  if (rootEl.value) ro.observe(rootEl.value);
  if (setAEl.value) ro.observe(setAEl.value);

  rafId = requestAnimationFrame(frame);
});

onBeforeUnmount(() => {
  if (rafId != null) cancelAnimationFrame(rafId);
  ro?.disconnect();
  ro = null;
  if (reducedMotionQuery) removeReducedMotionListener(reducedMotionQuery);
  reducedMotionQuery = null;
});
</script>
